import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import appBackground from '../../assets/app-background.webp';
import { NavigationGuard } from '../unsaved/unsaved';
import ButtonBase from '@mui/material/ButtonBase';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import { ChevronsUpDown, KeyRound, LogOut, Menu as MenuIcon, ScrollText, ShieldCheck, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { TruncatedText } from '../TruncatedText';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useLogout, useMe } from '../../api/auth';
import { useOrganisationList } from '../../api/users';
import logo from '../../assets/infinity-ev-black.png';
import { colours, radius } from '../../theme';
import { NAV } from './nav';
import { VersionLabel } from './VersionHistory';
import { useLiveUpdates } from '../../api/live';
import pulsepointLogo from '../../assets/pulsepoint-logo.png';

const SIDEBAR = 248;

/**
 * How strongly the background photo (assets/app-background.webp) shows through: 0 is plain
 * white, 1 is the photo at full strength. Keep it low so text and tables read cleanly.
 */
const BACKGROUND_STRENGTH = 0.08;

/** Printing (or saving as PDF) shows the page only: no menu, no buttons marked no-print, nothing cut off by scrolling. */
const PRINT = {
  '@media print': {
    'nav, header, .skip-link, .no-print': { display: 'none !important' },
    'body, #root, #root > div, main': { height: 'auto !important', overflow: 'visible !important', display: 'block !important' },
    'main > div': { padding: '0 !important', maxWidth: 'none !important' },
    '@page': { size: 'A4 landscape', margin: '12mm' },
  },
};

/**
 * The signed-in app: sidebar (a drawer on phones and tablets) and the page. The window
 * itself never scrolls: only the page area does, up and down and sideways, so the menu
 * (and on phones the top bar) always stays put.
 */
export function AppLayout() {
  const desktop = useMediaQuery((t: Theme) => t.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const main = useRef<HTMLElement>(null);
  const { data: me } = useMe();
  useLiveUpdates(!!me?.permissions.includes('chargers.view'));
  useEffect(() => {
    setOpen(false);
    main.current?.scrollTo(0, 0); // a new page starts at the top
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: desktop ? 'row' : 'column', height: '100dvh', overflow: 'hidden' }}>
      <GlobalStyles styles={PRINT} />
      <NavigationGuard />
      <a href="#main" className="skip-link">Skip to content</a>
      {desktop ? (
        <Box component="nav" aria-label="Main" sx={{ width: SIDEBAR, flexShrink: 0, borderRight: `1px solid ${colours.hairline}`, height: '100%', overflowY: 'auto' }}>
          <Sidebar />
        </Box>
      ) : (
        <>
          <Box component="header" sx={{ flexShrink: 0, height: 60, bgcolor: 'background.default', borderBottom: `1px solid ${colours.hairline}`, display: 'flex', alignItems: 'center', px: 1.5, gap: 1 }}>
            <IconButton aria-label="Open menu" onClick={() => setOpen(true)}>
              <MenuIcon size={24} strokeWidth={1.5} />
            </IconButton>
            <Wordmark compact />
          </Box>
          <Drawer open={open} onClose={() => setOpen(false)} slotProps={{ paper: { sx: { width: 280 } } }}>
            <Box component="nav" aria-label="Main" sx={{ height: '100%' }}>
              <Sidebar onClose={() => setOpen(false)} />
            </Box>
          </Drawer>
        </>
      )}
      <Box
        component="main"
        id="main"
        ref={main}
        tabIndex={-1}
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'auto',
          outline: 'none',
          // The Infinity EV 3D logo behind every page, faded by a white layer. Cards and tables stay white on top.
          bgcolor: colours.white,
          backgroundImage: `linear-gradient(rgba(255,255,255,${1 - BACKGROUND_STRENGTH}), rgba(255,255,255,${1 - BACKGROUND_STRENGTH})), url(${appBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll', // stays put while the page scrolls over it
          '@media print': { backgroundImage: 'none' },
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: 'auto', px: { xs: 2.5, md: 6 }, pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

/** The Pulsepoint logo; smaller in the phone top bar. */
function Wordmark({ compact }: { compact?: boolean }) {
  return (
    <Box
      component="img"
      src={pulsepointLogo}
      alt="Pulsepoint"
      sx={{ display: 'block', height: 'auto', width: compact ? 120 : 196, maxWidth: '100%' }}
    />
  );
}

function Sidebar({ onClose }: { onClose?: () => void }) {
  const { data: me } = useMe();
  const items = NAV.filter((n) => !n.permission || me?.permissions.includes(n.permission));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', px: 2, pt: 3.5, pb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 1, mb: 4 }}>
        <Wordmark />
        {onClose && (
          <IconButton aria-label="Close menu" onClick={onClose} sx={{ mt: -0.5 }}>
            <X size={22} strokeWidth={1.5} />
          </IconButton>
        )}
      </Box>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
        {items.map(({ label, to, icon: Icon }) => (
          <li key={to}>
            <Box
              component={NavLink}
              to={to}
              end={to === '/'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                height: 44,
                px: 1.5,
                borderRadius: `${radius.panel}px`,
                color: 'text.primary',
                textDecoration: 'none',
                fontWeight: 400,
                fontSize: 17,
                '&:hover': { bgcolor: colours.paleGrey },
                '&.active': { bgcolor: colours.paleGrey, fontWeight: 500 },
              }}
            >
              <Icon size={20} strokeWidth={1.5} aria-hidden />
              {label}
            </Box>
          </li>
        ))}
      </Box>
      <Box sx={{ flex: 1 }} />
      <VersionLabel />
      <AccountButton />
      <Box component="img" src={logo} alt="Infinity EV" sx={{ width: 140, height: 'auto', mt: 2.5, ml: 1, display: 'block' }} />
    </Box>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts.at(-1)![0] : '')).toUpperCase();
}

/** Who is signed in, with Change password and Sign out. */
function AccountButton() {
  const { data: me } = useMe();
  const orgs = useOrganisationList();
  const logout = useLogout();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!me) return null;

  const orgLine =
    me.role === 'admin'
      ? 'Admin, all organisations'
      : me.organisationIds?.length === 1
        ? (orgs.data?.find((o) => o.id === me.organisationIds![0])?.businessName ?? '')
        : `${me.organisationIds?.length ?? 0} organisations`;

  return (
    <Box sx={{ borderTop: `1px solid ${colours.hairline}`, pt: 2 }}>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-label={`Account: ${me.displayName}, ${orgLine}`}
        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: `${radius.panel}px`, textAlign: 'left', '&:hover': { bgcolor: colours.paleGrey } }}
      >
        <Avatar sx={{ width: 40, height: 40, bgcolor: colours.paleGrey, color: colours.nearBlack, fontSize: 15, fontWeight: 500 }}>{initials(me.displayName)}</Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <TruncatedText text={me.displayName} sx={{ fontWeight: 500 }} />
          <TruncatedText text={orgLine} variant="body2" color="textSecondary" />
        </Box>
        <ChevronsUpDown size={16} strokeWidth={1.5} color={colours.graphite} aria-hidden />
      </ButtonBase>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        {me.permissions.includes('audit.view') && (
          <MenuItem onClick={() => { setAnchor(null); navigate('/audit'); }}>
            <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><ScrollText size={18} strokeWidth={1.5} /></ListItemIcon>
            Audit log
          </MenuItem>
        )}
        <MenuItem onClick={() => { setAnchor(null); navigate('/settings/account'); }}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><KeyRound size={18} strokeWidth={1.5} /></ListItemIcon>
          Change password
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate('/settings/account#two-factor'); }}>
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><ShieldCheck size={18} strokeWidth={1.5} /></ListItemIcon>
          Two-factor sign-in
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            logout.mutate(undefined, { onSettled: () => navigate('/sign-in', { replace: true }) });
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}><LogOut size={18} strokeWidth={1.5} /></ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
}
